/**
 * cxGuardForm — the pure pieces of the Stock-In Safety Engine's place in StockInForm.
 *
 * The form keeps only the wiring (refs, the STEP 8.7 call, the popup mount); everything
 * that decides something lives here so it can be tested without rendering 12,000 lines:
 *
 *   cxPrint / cxLinesHash   what a line looked like when it was judged (stale-token check)
 *   cxTokensFor             a popup decision → the EXISTING confirmation tokens (rule 4)
 *   cxWriteBack             popup values → the row, the way typing into the row would
 *   cxFilterWarnings        STEP 9: a pre-validate price warning the popup already answered
 *   cxRecordPayload         after a save: what was decided, per saved line
 *   cxRowAid                the quiet "= ₹40 / kg" and "Last time: …" under a row
 */
import { cxCheckLine, displayPrice, FAMILY_DISPLAY } from './cxCheckLine';

const r2 = (n) => Math.round(Number(n) * 100) / 100;
const r4 = (n) => Math.round(Number(n) * 10000) / 10000;

/** A line's identity for "has this row changed since it was judged?". */
export function cxPrint(line) {
  if (!line) return '';
  const pack = line.pack ? `${r4(line.pack.quantity)}${line.pack.unit}` : '';
  return [line.item_id || '', String(line.item_name || '').trim().toLowerCase(), r4(line.quantity), line.unit, r2(line.total), pack].join('|');
}

export function cxLinesHash(lines) {
  return (lines || []).map((l) => `${l.row_key}=${cxPrint(l)}`).join('\n');
}

/** Decisions that answered a question — the ones that carry tokens and are never asked twice. */
export const CX_ANSWERED = new Set(['amber_confirmed', 'red_corrected', 'red_forced']);

/**
 * The tokens the existing gate / DSF already understand, for one decided line.
 * Each carries rowId (re-pointed at submit by remapConfirmationsToRows) plus `cx` and
 * `cxPrint` so STEP 8.7 can drop exactly its own tokens when the row is edited later.
 * The backend's Joi schema strips the extra keys (stripUnknown).
 */
export function cxTokensFor({ decision, uid, index, print, check, forcedName }) {
  if (!CX_ANSWERED.has(decision)) return [];
  const existing = (check && check.existing) || {};
  const qZone = check && check.quantity ? check.quantity.zone : null;
  const qtyInvolved = qZone === 'amber' || qZone === 'red' || existing.gate === 'block_qty';
  const base = { index, rowId: uid, cx: true, cxPrint: print };
  if (decision === 'amber_confirmed') {
    const out = [{ ...base, type: 'price_acknowledged' }];
    if (qtyInvolved) out.push({ ...base, type: 'quantity_acknowledged' });
    return out;
  }
  if (decision === 'red_corrected') return [{ ...base, type: 'correction_applied' }];
  // red_forced — the typed item name is the acknowledgment the gate requires (≤200 chars).
  const acknowledgment = String(forcedName || '').trim().slice(0, 200) || 'CONFIRMED BY OWNER';
  const out = [
    { ...base, type: 'extreme_price_acknowledged', acknowledgment },
    { ...base, type: 'price_acknowledged', acknowledgment },
  ];
  if (qtyInvolved) {
    out.push({ ...base, type: 'extreme_quantity_acknowledged', acknowledgment });
    out.push({ ...base, type: 'quantity_acknowledged', acknowledgment });
  }
  return out;
}

/**
 * The popup's final values written into a form row — as typing into the row would.
 *
 *   quantity  the popup judges paid + free (what the gate judges); both are scaled by the
 *             same factor, so a 1000× slip fixes the free goods too
 *   unit      never changed on a packed row (the pack size, not the unit, measures it)
 *   total     the popup judges the GROSS line total (grossLineTotal). On a taxed row the
 *             gross goes into "Total Price With Tax" and the net is derived, exactly the
 *             handleInputChange('total_price_with_tax') path; otherwise into total_price.
 *   unit_price  re-derived (TOTAL WINS); the with-tax mirror is left alone unless the
 *             total itself changed, so a quantity fix never moves the money by a paisa.
 *
 * @param {object} row
 * @param {{quantity:number, unit:string, total:number}} values
 * @param {{ judged:{quantity:number, unit:string, total:number, pack?:object}, taxSlabActive:boolean,
 *           baseFromGross:(gross:number, slab:string)=>string }} ctx
 */
export function cxWriteBack(row, values, { judged, taxSlabActive, baseFromGross }) {
  const next = { ...row };
  const q0 = Number(parseFloat(row.quantity) || 0);
  const f0 = Number(parseFloat(row.free_quantity) || 0);
  if (r4(values.quantity) !== r4(judged.quantity) && Number(values.quantity) > 0) {
    if (f0 > 0 && q0 + f0 > 0) {
      const k = Number(values.quantity) / (q0 + f0);
      next.quantity = String(r4(q0 * k));
      next.free_quantity = String(r4(f0 * k));
    } else {
      next.quantity = String(r4(values.quantity));
    }
  }
  if (!judged.pack && values.unit && values.unit !== judged.unit) next.unit = values.unit;
  const totalChanged = r2(values.total) !== r2(judged.total) && Number(values.total) > 0;
  if (totalChanged) {
    if (taxSlabActive) {
      next.total_price_with_tax = String(r2(values.total));
      next.total_price = baseFromGross(r2(values.total), row.tax_slab);
    } else {
      next.total_price = String(r2(values.total));
    }
  }
  const q = parseFloat(next.quantity);
  const t = parseFloat(next.total_price);
  next.unit_price = q > 0 && t > 0 ? (t / q).toFixed(4) : '';
  return next;
}

/** STEP 9: drop pre-validate price warnings for rows the popup already settled. Errors are never touched. */
export function cxFilterWarnings(warnings, rows, resolvedUids) {
  if (!Array.isArray(warnings) || !resolvedUids || resolvedUids.size === 0) return warnings;
  return warnings.filter((w) => {
    if (!w || w.type !== 'price_anomaly') return true;
    const row = rows[Number(w.index)];
    return !(row && resolvedUids.has(row.uid));
  });
}

/**
 * After a save: one entry per judged line still looking exactly as judged.
 * `lineItems` is the submit response's line_items (request order); the server checks the
 * item id against the saved line, so a misaligned position is skipped, never mis-recorded.
 */
export function cxRecordPayload({ stockInId, rows, decisions, printOf, lineItems }) {
  if (!stockInId || !Array.isArray(rows) || !decisions || decisions.size === 0) return null;
  const aligned = Array.isArray(lineItems) && lineItems.length === rows.length;
  const lines = [];
  rows.forEach((row, index) => {
    const d = row && decisions.get(row.uid);
    if (!d || !d.item_id || d.print !== printOf(row)) return;
    const li = aligned ? lineItems[index] : null;
    lines.push({
      index,
      line_item_id: li && li.line_item_id ? Number(li.line_item_id) : undefined,
      item_id: d.item_id,
      row_key: row.uid,
      decision: d.decision,
      entered: d.entered,
      window_version: d.window_version,
      forced_name: d.forced_name || undefined,
    });
  });
  return lines.length ? { stock_in_id: Number(stockInId), lines: lines.slice(0, 100) } : null;
}

/**
 * The quiet aids under a matched row (design §4.1). Null when there is nothing to say.
 * `warn` only when the price is more than hint_factor (10×) outside the category range —
 * the backtest showed tighter hints misfire on miscategorised items.
 */
export function cxRowAid({ quantity, unit, total, pack, ctx }) {
  if (!ctx || !ctx.family) return null;
  const fam = FAMILY_DISPLAY[ctx.family];
  let perDisplay = null;
  if (Number(quantity) > 0 && Number(total) > 0) {
    const v = cxCheckLine({ quantity: Number(quantity), unit, total: Number(total), pack }, { item: { family: ctx.family }, window: null }, { fixes: false });
    if (v.base && v.base.family === ctx.family && v.base.price > 0) perDisplay = displayPrice(v.base.price, ctx.family);
  }
  let warn = false;
  if (perDisplay != null && ctx.range && ctx.range.low > 0 && ctx.range.high > 0) {
    const f = ctx.range.hint_factor || 10;
    warn = perDisplay > ctx.range.high * f || perDisplay < ctx.range.low / f;
  }
  const last = ctx.last && ctx.last.per_display > 0 ? ctx.last : null;
  if (perDisplay == null && !last) return null;
  return { perDisplay, unit: fam ? fam.unit : '', warn, last };
}

/**
 * The unit this item was last bought in, for the unit default at the three match sites:
 * `unitAfterMatch(row.unit || cxLastUnit(store, inv), invUnit)`. The store is only ever
 * filled while the guard is on, so with it off this is always '' and the call is
 * byte-for-byte today's. Reads a plain object (a ref's value) so it can sit inside an
 * effect without becoming a dependency.
 */
export function cxLastUnit(store, inv) {
  if (!store || !inv) return '';
  const byId = inv.item_id != null && store.byId ? store.byId[Number(inv.item_id)] : undefined;
  const c = byId || (store.byName ? store.byName[String(inv.name || inv.item_name || '').trim().toLowerCase()] : undefined);
  return (c && c.last_unit) || '';
}

/** What was entered, as record-decisions wants it (base_qty includes pack sizes). */
export function cxEntered(line, family) {
  const v = cxCheckLine({ quantity: line.quantity, unit: line.unit, total: line.total, pack: line.pack || undefined },
    { item: { family }, window: null }, { fixes: false });
  return { quantity: line.quantity, unit: line.unit, total: r2(line.total), base_qty: v.base ? v.base.qty : null };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** '2026-09-18' → '18 Sep' — read as a calendar day, never through Date (no UTC shift). */
export function cxDay(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}` : '';
}

export const cxMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: Number(n) >= 100 ? 0 : 2 })}`;
